import type { ElementType } from 'react';

import { AICOACH_FEEDBACK, MESSAGE_SENDER } from 'bundles/ai-course-coach/components/constants';
import type {
  MessagesActions,
  ResponseMessage,
  UpdateChatHistory,
  UserMessage,
} from 'bundles/ai-course-coach/store/actions/types';
import type { LinkParams, MessagesState } from 'bundles/ai-course-coach/store/types';
import { createReducer } from 'bundles/ai-course-coach/utils/createReducer';
import { createComponentMessage, createLinkSnippet, createNewMessage } from 'bundles/ai-course-coach/utils/messages';

import {
  ADD_COMPONENT_MESSAGE,
  ADD_NEW_LINK_SNIPPET,
  ADD_NEW_RESPONSE_MESSAGE,
  ADD_NEW_USER_MESSAGE,
  DELETE_MESSAGES,
  DROP_MESSAGES,
  FEEDBACK_SET_LIKE,
  FEEDBACK_SET_UNLIKE,
  FEEDBACK_UNSET,
  HIDE_AVATAR,
  MARK_ALL_READ,
  SET_BADGE_COUNT,
  SET_CHAT_SESSION_ID,
  UPDATE_CHAT_HISTORY,
} from '../actions/types';

const initialState = {
  messages: [],
  chatSessionId: null,
  historicalMessagesMap: {},
  badgeCount: 0, // number of unread messages not generated by the user
};
// @ts-disable
// @eslint-disable
const messagesReducer = {
  [UPDATE_CHAT_HISTORY]: (state: MessagesState, { messages }: { messages: UpdateChatHistory['messages'] }) => {
    const formattedMessages = messages.map((message) => {
      return createNewMessage(message);
    });

    return {
      ...state,
      messages: formattedMessages,
      historicalMessagesMap: {
        ...state.historicalMessagesMap,
        [state.chatSessionId || '']: formattedMessages,
      },
    };
  },
  [ADD_NEW_USER_MESSAGE]: (state: MessagesState, { text, id, timestamp, action }: UserMessage) => {
    const messages = [
      ...state.messages,
      createNewMessage({ text, sender: MESSAGE_SENDER.CLIENT, id, timestamp, action }),
    ];
    return {
      ...state,
      messages,
      historicalMessagesMap: {
        ...state.historicalMessagesMap,
        [state.chatSessionId || '']: messages,
      },
    };
  },
  [ADD_NEW_RESPONSE_MESSAGE]: (
    state: MessagesState,
    { text, id, clipRecommendations, userFeedback, timestamp, recommendedActions, navigationButtons }: ResponseMessage
  ) => {
    const messages = [
      ...state.messages,
      createNewMessage({
        text,
        sender: MESSAGE_SENDER.RESPONSE,
        id,
        clipRecommendations,
        userFeedback,
        timestamp,
        recommendedActions,
        navigationButtons,
      }),
    ];
    return {
      ...state,
      messages,
      historicalMessagesMap: {
        ...state.historicalMessagesMap,
        [state.chatSessionId || '']: messages,
      },
      badgeCount: state.badgeCount + 1,
    };
  },

  [ADD_NEW_LINK_SNIPPET]: (state: MessagesState, { link, id }: { link: LinkParams; id?: string }) => {
    const messages = [...state.messages, createLinkSnippet(link, id)];
    return {
      ...state,
      messages,
      historicalMessagesMap: {
        ...state.historicalMessagesMap,
        [state.chatSessionId || '']: messages,
      },
    };
  },
  [ADD_COMPONENT_MESSAGE]: (
    state: MessagesState,
    {
      component,
      props,
      showAvatar,
      id,
    }: { component: ElementType<any>; props: any; showAvatar: boolean; id?: string | undefined }
  ) => {
    const messages = [...state.messages, createComponentMessage(component, props, showAvatar, id)];
    return {
      ...state,
      messages,
      historicalMessagesMap: {
        ...state.historicalMessagesMap,
        [state.chatSessionId || '']: messages,
      },
    };
  },

  [DROP_MESSAGES]: (state: MessagesState) => ({
    ...state,
    messages: [],
    historicalMessagesMap: {
      ...state.historicalMessagesMap,
      [state.chatSessionId || '']: [],
    },
    badgeCount: 0,
  }),

  [HIDE_AVATAR]: (state: MessagesState, { index }: { index: number }) => {
    state.messages[index].showAvatar = false;
    const messagesForCurrentSession = state.historicalMessagesMap[state.chatSessionId || ''];
    if (messagesForCurrentSession) {
      messagesForCurrentSession[index].showAvatar = false;
    }
  },
  /**
   * The following function deletes a certain number of messages, where the
   * number of messages to delete is the input `count`.
   *
   * When the input `id` is available, the last message to delete is the one
   * whose customId matches `id`; otherwise, the last message to delete is the
   * last message in the list
   */
  // @ts-expect-error
  [DELETE_MESSAGES]: (state: MessagesState, { count, id }) => {
    const remainingMessages = id
      ? state.messages.filter((_, index) => {
          const targetMsgIndex = state.messages.findIndex((tMsg) => tMsg.customId === id);
          return index < targetMsgIndex - count + 1 || index > targetMsgIndex;
        })
      : state.messages.slice(0, state.messages.length - count);

    return {
      ...state,
      messages: remainingMessages,
      historicalMessagesMap: {
        ...state.historicalMessagesMap,
        [state.chatSessionId || '']: remainingMessages,
      },
      badgeCount: remainingMessages.filter((message) => message.unread).length,
    };
  },
  // @ts-expect-error
  [SET_BADGE_COUNT]: (state: MessagesState, { count }) => ({ ...state, badgeCount: count }),

  [MARK_ALL_READ]: (state: MessagesState) => {
    const messages = state.messages.map((message) => ({ ...message, unread: false }));
    return {
      ...state,
      messages,
      historicalMessagesMap: {
        ...state.historicalMessagesMap,
        [state.chatSessionId || '']: messages,
      },
      badgeCount: 0,
    };
  },
  // @ts-expect-error
  [FEEDBACK_SET_LIKE]: (state: MessagesState, { id }) => {
    const messages = state.messages.map((message) => ({
      ...message,
      userFeedback: id === message.customId ? AICOACH_FEEDBACK.like : message.userFeedback,
    }));
    return {
      ...state,
      messages,
      historicalMessagesMap: {
        ...state.historicalMessagesMap,
        [state.chatSessionId || '']: messages,
      },
    };
  },
  // @ts-expect-error
  [FEEDBACK_SET_UNLIKE]: (state: MessagesState, { id }) => {
    const messages = state.messages.map((message) => ({
      ...message,
      userFeedback: id === message.customId ? AICOACH_FEEDBACK.dislike : message.userFeedback,
    }));
    return {
      ...state,
      messages,
      historicalMessagesMap: {
        ...state.historicalMessagesMap,
        [state.chatSessionId || '']: messages,
      },
    };
  },
  // @ts-expect-error
  [FEEDBACK_UNSET]: (state: MessagesState, { id }) => {
    const messages = state.messages.map((message) => ({
      ...message,
      userFeedback: id === message.customId ? AICOACH_FEEDBACK.unset : message.userFeedback,
    }));
    return {
      ...state,
      messages,
      historicalMessagesMap: {
        ...state.historicalMessagesMap,
        [state.chatSessionId || '']: messages,
      },
    };
  },
  [SET_CHAT_SESSION_ID]: (state: MessagesState, { chatSessionId }: { chatSessionId: string | null }) => ({
    ...state,
    chatSessionId,
  }),
};
// @ts-enable
// @eslint-enable
export default (state = initialState, action: MessagesActions) => createReducer(messagesReducer, state, action);
